// src/components/Groups/AddGroupTypeDialog.tsx

import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import apiAxiosInstance from "../../utils/axiosInstance";

interface GroupType {
  _id: string;
  name: string;
}

interface AddGroupTypeDialogProps {
  open: boolean;
  onClose: () => void;
  onGroupTypeAdded: (groupType: GroupType) => void;
}

const AddGroupTypeDialog: React.FC<AddGroupTypeDialogProps> = ({
  open,
  onClose,
  onGroupTypeAdded,
}) => {
  const [newTypeName, setNewTypeName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newTypeName.trim()) {
      setError("Group type name is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await apiAxiosInstance.post<GroupType>("/api/group-types", {
        name: newTypeName.trim(),
      });
      
      onGroupTypeAdded(response.data);
      setNewTypeName("");
      onClose();
    } catch (error: any) {
      console.error("Error creating group type:", error);
      setError(
        error.response?.data?.error || 
        error.response?.data?.message || 
        "Failed to create group type. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewTypeName("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Group Type</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Create a new group type that can be used when creating groups.
          </Typography>
          
          <TextField
            autoFocus
            label="Group Type Name"
            value={newTypeName}
            onChange={(e) => {
              setNewTypeName(e.target.value);
              if (error) setError(""); // Clear error when user starts typing
            }}
            fullWidth
            required
            margin="normal"
            error={!!error}
            helperText={error}
            placeholder="e.g., Reading Intervention, Math Support, etc."
          />
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} color="inherit" disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || !newTypeName.trim()}
          >
            {loading ? <CircularProgress size={24} /> : "Add Group Type"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddGroupTypeDialog;
